import React, { useState } from "react";
import { useQuery } from "../../hooks/useQuery";
import { api } from "../../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Printer, Ban, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Requested", "In Transit", "Out for Delivery", "Delivered", "Voided"];

export default function Shipments() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const { data, loading, reload } = useQuery(() => api.get("/shipments").then((r) => r.data));
  const [q, setQ] = useState("");
  const shipments = (data || []).filter((s) => !q || s.tracking_id.includes(q.toUpperCase()) || (s.receiver_name || "").toLowerCase().includes(q.toLowerCase()));

  const overrideStatus = async (tid, status) => {
    if (!can("can_override_status")) {
      toast.error("Permission denied: can_override_status");
      return;
    }
    try {
      await api.post(`/shipments/${tid}/override-status`, null, { params: { status } });
      toast.success(`${tid} → ${status}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const voidShipment = async (tid) => {
    if (!can("can_void_shipment_or_refund")) {
      toast.error("Permission denied");
      return;
    }
    if (!window.confirm(`Void shipment ${tid}? This cannot be undone.`)) return;
    try {
      await api.post(`/shipments/${tid}/void`);
      toast.success("Voided");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="shipments-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif-bv text-3xl text-[#1B3022]">Manifests</h1>
          <p className="text-sm text-[#1B3022]/60">All HBS shipments under your jurisdiction.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reload} className="btn-outline-gold rounded-sm px-3 py-2 text-xs" style={{ borderColor: "#1B3022", color: "#1B3022" }}>
            <RefreshCw size={12} className="inline mr-1" /> Refresh
          </button>
          {can("can_create_shipments") && (
            <Link to="/admin/shipments/new" data-testid="new-shipment-btn" className="btn-gold rounded-sm px-4 py-2 text-xs">+ New shipment</Link>
          )}
        </div>
      </div>

      <input
        data-testid="ship-search"
        placeholder="Search by HBS# or receiver…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="mt-4 w-full max-w-md px-4 py-2 border border-[#1B3022]/30 bg-white rounded-sm text-sm"
      />

      <div className="mt-6 bg-white border border-[#1B3022]/15 rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="shipments-table">
          <thead className="bg-[#0F1D14] text-[#D4AF37]">
            <tr>
              {["Tracking", "Sender", "Receiver", "Method", "Status", "Created", "Actions"].map((h) => (
                <th key={h} className="px-3 py-3 text-left text-[10px] tracking-[0.2em] uppercase font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-12 text-center text-[#1B3022]/60"><Loader2 className="inline animate-spin mr-2" size={14} />Loading…</td></tr>}
            {!loading && shipments.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-[#1B3022]/60">No shipments.</td></tr>}
            {shipments.map((s) => (
              <tr key={s.tracking_id} className="border-t border-[#1B3022]/10 hover:bg-[#F5EFE0]/40">
                <td className="px-3 py-2 font-mono text-xs tracking-widest text-[#1B3022]">{s.tracking_id}</td>
                <td className="px-3 py-2 text-[#1B3022]/85">{s.sender_name}</td>
                <td className="px-3 py-2 text-[#1B3022]/85">{s.receiver_name}</td>
                <td className="px-3 py-2 text-[#1B3022]/85 text-xs">{s.method}</td>
                <td className="px-3 py-2">
                  <select
                    value={s.current_status}
                    onChange={(e) => overrideStatus(s.tracking_id, e.target.value)}
                    disabled={!can("can_override_status") || s.voided}
                    className="px-2 py-1 border border-[#1B3022]/30 bg-white rounded-sm text-xs disabled:opacity-50"
                    data-testid={`status-select-${s.tracking_id}`}
                  >
                    {STATUSES.map((st) => <option key={st}>{st}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2 text-[#1B3022]/60 text-xs">{(s.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    {can("can_reprint_labels") && (
                      <button
                        onClick={() => navigate(`/label/${s.tracking_id}`)}
                        data-testid={`label-${s.tracking_id}`}
                        title="Reprint label"
                        className="p-1.5 border border-[#D4AF37] rounded-sm hover:bg-[#D4AF37]/10"
                      >
                        <Printer size={12} className="text-[#A88420]" />
                      </button>
                    )}
                    {can("can_void_shipment_or_refund") && !s.voided && (
                      <button
                        onClick={() => voidShipment(s.tracking_id)}
                        title="Void"
                        className="p-1.5 border border-[#C8102E]/50 rounded-sm hover:bg-[#C8102E]/10"
                      >
                        <Ban size={12} className="text-[#C8102E]" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
